/**
 * useTimezoneFormatting Hook
 *
 * Combines the active factory's timezone with the app's current language so
 * date/time values render consistently in the factory's local time and the
 * user's chosen locale, instead of the browser's default timezone/locale.
 * Thin wrapper over the existing Luxon-based helpers in `@/utils/timezone`.
 */

import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';
import { useFactoryTimezoneValue } from './useFactoryTimezone';
import {
  formatTimeOnly,
  formatDateOnly,
  formatDateWithStyle,
  getUserDateFormatLocale,
} from '@/utils/timezone';

export type TimestampInput = string | number | Date;

interface TimezoneFormatting {
  timezone: string;
  locale: string;
  formatFactoryTime: (value: TimestampInput) => string;
  formatFactoryDate: (value: TimestampInput) => string;
  formatFactoryDateTime: (
    value: TimestampInput,
    style?: 'short' | 'medium' | 'long' | 'full'
  ) => string;
}

/**
 * Normalizes the three timestamp shapes found across the codebase (ISO
 * string, epoch-ms number, JS Date) into a UTC ISO string, which is what
 * `@/utils/timezone`'s formatters expect.
 */
function toUtcIso(value: TimestampInput): string {
  if (value instanceof Date) {
    return value.toISOString();
  }
  if (typeof value === 'number') {
    return DateTime.fromMillis(value).toUTC().toISO() ?? new Date(value).toISOString();
  }
  return value;
}

/**
 * Hook for formatting timestamps in the active factory's timezone, using the
 * app's current language for locale-aware output (e.g. date order, AM/PM).
 */
export function useTimezoneFormatting(): TimezoneFormatting {
  const timezone = useFactoryTimezoneValue();
  const { i18n } = useTranslation();
  const locale = getUserDateFormatLocale(i18n.language);

  return {
    timezone,
    locale,
    formatFactoryTime: (value): string => formatTimeOnly(toUtcIso(value), timezone, locale),
    formatFactoryDate: (value): string => formatDateOnly(toUtcIso(value), timezone, locale),
    formatFactoryDateTime: (value, style = 'medium'): string =>
      formatDateWithStyle(toUtcIso(value), timezone, style, locale),
  };
}
